import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import {
  getRepairCommonFaults,
  createRepairCommonFault,
  updateRepairCommonFault,
  deleteRepairCommonFault
} from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';

const RepairCommonFaultField = ({ value, onChange, disabled, setError, setSuccess }) => {
  const [options, setOptions] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getRepairCommonFaults();
      setOptions(res.data || []);
    } catch (_) {
      setOptions([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAsCommon = async () => {
    const text = (value || '').trim();
    if (!text) {
      setError?.('Enter a fault description first');
      return;
    }
    try {
      setSaving(true);
      await createRepairCommonFault({ description: text });
      setSuccess?.('Saved as a common fault');
      load();
    } catch (err) {
      setError?.(getErrorMessage(err, 'Failed to save common fault'));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    const text = editText.trim();
    if (!text) return;
    try {
      setSaving(true);
      await updateRepairCommonFault(editRow.id, { description: text });
      if ((value || '').trim() === (editRow.description || '').trim()) {
        onChange(text);
      }
      setEditRow(null);
      setEditText('');
      setSuccess?.('Common fault updated');
      load();
    } catch (err) {
      setError?.(getErrorMessage(err, 'Failed to update common fault'));
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm('Remove this common fault?')) return;
    try {
      await deleteRepairCommonFault(row.id);
      load();
    } catch (err) {
      setError?.(getErrorMessage(err, 'Failed to remove common fault'));
    }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options.map((o) => o.description)}
        value={value || ''}
        onChange={(_, v) => onChange(v || '')}
        onInputChange={(_, v, reason) => {
          if (reason === 'input' || reason === 'clear') onChange(v || '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Fault description"
            multiline
            minRows={3}
            helperText="Type a fault, or pick a saved common fault"
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<BookmarkAddIcon />}
          disabled={disabled || saving || !(value || '').trim()}
          onClick={saveAsCommon}
        >
          Save as common
        </Button>
        <Button size="small" variant="text" startIcon={<EditIcon />} onClick={() => setManageOpen(true)}>
          Edit common faults
        </Button>
      </Box>

      <Dialog open={manageOpen} onClose={() => { setManageOpen(false); setEditRow(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Common fault descriptions</DialogTitle>
        <DialogContent>
          {options.length === 0 ? (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              No saved faults yet. Type one and click Save as common.
            </Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell width={90} />
                </TableRow>
              </TableHead>
              <TableBody>
                {options.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {editRow?.id === row.id ? (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editRow?.id === row.id ? (
                        <Button size="small" disabled={saving} onClick={saveEdit}>Save</Button>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => { setEditRow(row); setEditText(row.description || ''); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManageOpen(false); setEditRow(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepairCommonFaultField;
